import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as transactionsSelectors from 'store/transactionsSlice/transactionsSelectors';

let globalSubscribers = new Set();
let globalInterval = null;

const startGlobalInterval = () => {
    if (!globalInterval) {
        globalInterval = setInterval(() => {
            const now = Date.now();
            globalSubscribers.forEach((cb) => cb(now));
        }, 1000);
    }
};

const stopGlobalInterval = () => {
    if (globalInterval && globalSubscribers.size === 0) {
        clearInterval(globalInterval);
        globalInterval = null;
    }
};

export const subscribeToGlobalTick = (cb) => {
    globalSubscribers.add(cb);
    startGlobalInterval();
    return () => {
        globalSubscribers.delete(cb);
        stopGlobalInterval();
    };
};

const getBackground = (seconds) => {
    if (seconds >= 120) return '#F94343';
    if (seconds >= 60) return '#EF6C00';
    return '#5B5B5B38';
};

const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return [m, s].map((n) => n.toString().padStart(2, '0')).join(':');
};

const useGlobalTransactionTimer = (transactionStartTimestamp, options = {}) => {
    const { ignoreActiveTab = false } = options;
    const [currentTime, setCurrentTime] = useState(Date.now());
    const activeTab = useSelector(transactionsSelectors.selectActiveTab);
    const isActive = !!transactionStartTimestamp && (ignoreActiveTab || activeTab === 'wall');

    const updateTime = useCallback((now) => setCurrentTime(now), []);

    useEffect(() => {
        if (!isActive) return;
        globalSubscribers.add(updateTime);
        startGlobalInterval();
        return () => {
            globalSubscribers.delete(updateTime);
            stopGlobalInterval();
        };
    }, [isActive, updateTime]);

    const totalSeconds = useMemo(
        () => (isActive ? Math.max(0, Math.floor((currentTime - transactionStartTimestamp) / 1000)) : 0),
        [isActive, currentTime, transactionStartTimestamp]
    );

    return { totalSeconds };
};

export { getBackground, formatTime };
export default useGlobalTransactionTimer;
